import * as React from 'react';
import "./TextElement.scss";
import { useCallback, useMemo, useState, ChangeEvent, useEffect, FocusEvent } from 'react';
import { TextProps, TextDefines } from './IText';
import InputElement from '../core/InputElement';
import { ElementType } from '../../common/enum/elementType';
import { InputType } from './types/input';
import useActionExecutor from '../service/actionExecutor';
import PostalCode from './PostalCode';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import { ColorDefines } from '../../interface/IColors';
import { replaceStringValue } from '../../util/replaceStringValue';

interface TextElementProps {
  props: TextProps;
  defines: TextDefines;
  colors: ColorDefines;
}

interface InputPattern {
  type: string;
  mode?: "text" | "email" | "tel" | "numeric" | "none" | "url" | "decimal" | "search";
  style?: string;
  format?: (v: string) => string;
}

const toHalf = (str: string) => {
  const symbolReg = new RegExp(/[＠。ー＋]/);
  return str.replace(/[Ａ-Ｚａ-ｚ０-９＠。ー＋]/g, (s: string) => {
    if (symbolReg.test(s)) {
      if (s === "＠") return "@";
      if (s === "。") return ".";
      if (s === "ー") return "-";
      if (s === "＋") return "+";
      return "";
    } else {
      return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
    }
  });
};

const phoneUtil = PhoneNumberUtil.getInstance();

const TextElement = ({ props, defines, colors }: TextElementProps) => {
  const [touched, setTouched] = useState(false);
  const [value, setValue] = useState(props.value ||
    (props.getData ?
      replaceStringValue(defines.defaultValue, props.getData().data.input || {}) :
      defines.defaultValue
    )
  );
  const [rawValue, setRawValue] = useState<string>(props.value || defines.defaultValue || '');
  const [composing, setComposing] = useState<boolean>(false);
  const { click, change, data } = useActionExecutor(props.event, defines);

  const pattern: InputPattern = useMemo(() => {
    if (!defines.option?.type) return null;
    switch (defines.option.type) {
      case InputType.EMAIL:
        return {
          type: "email",
          mode: "email",
          format: toHalf,
        };
      case InputType.TEL:
        return {
          type: "tel",
          mode: "tel",
          format: v => {
            try {
              const phoneNumber = phoneUtil.parseAndKeepRawInput(toHalf(v), 'JP');
              return phoneUtil.format(phoneNumber, PhoneNumberFormat.NATIONAL);
            } catch (e) {
              // console.log(e, v);
              return v;
            }
          },
        };
      case InputType.POSTAL_CODE:
        return {
          type: "text",
          mode: "numeric",
          format: toHalf,
          style: defines.option?.style
        };
      case InputType.HIRAGANA:
        return {
          type: "text",
        };
      default:
        return null;
    }
  }, [defines?.option?.type]);

  const setTouchedWrapper = (v: boolean) => {
    setTouched(v);
    if (props.setTouched) props.setTouched(v);
  };

  const oncompositionstart = useCallback(() => {
    setComposing(true);
  }, []);
  const oncompositionend = useCallback(() => {
    setComposing(false);
    if (pattern?.format != null) {
      requestAnimationFrame(() => setValue(pattern.format(rawValue)));
    }
  }, [pattern, rawValue]);

  const onchange = useCallback((e: ChangeEvent<HTMLInputElement>, _v?: string) => {
    setTouchedWrapper(true);
    let v = _v || e.target.value;
    setRawValue(v);

    if (pattern?.format != null && !composing) {
      v = pattern.format(v);
    }

    setValue(v);
    if (change) change(e);
  }, [pattern, composing]);

  const onfocus = (e: FocusEvent<HTMLInputElement>) => {
    const root = document.getElementById("chatform-root");
  }

  return (
    <InputElement
      type={ElementType.TEXT}
      value={value}
      touched={touched}
      dispatchValid={props.dispatchValid}
      composing={composing}
      {...{ props, defines }}
    >
      {defines?.option?.type === InputType.POSTAL_CODE && pattern.style == "split" ?
      <PostalCode
        name={defines.name}
        value={value}
        placeholder={defines.placeholder}
        onChangeValue={(e, v) => {
          onchange(e, v);
        }}
        setTouched={setTouched}
        setValue={setValue}
        onBlur={_ => setTouchedWrapper(true)}
      ></PostalCode> :
      <input
        name={defines.name}
        type={pattern?.type || "text"}
        inputMode={pattern?.mode || "text"}
        placeholder={defines.placeholder}
        readOnly={!!defines.readonly}
        value={value}
        onFocus={onfocus}
        onBlur={_ => setTouchedWrapper(true)}
        onCompositionStart={oncompositionstart}
        onCompositionEnd={oncompositionend}
        onChange={onchange} />
      }
    </InputElement>
  );
}

export default TextElement;
